import axios from 'axios';
import { GOOGLE_DRIVE_API_KEY } from '../../../../constants/Constants';
import Toast from '../../utils/Toast';
import { DOCUMENT_TOAST } from '../../../../constants/toastConstant';

export const uploadFiles = async (fileIds, accessToken) => {
  const filePromises = fileIds.map(async (fileId) => {
    try {
      // Fetch file metadata
      const metadataResponse = await axios.get(
        `https://www.googleapis.com/drive/v3/files/${fileId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      const fileName = metadataResponse?.data?.name;

      // Fetch file content
      const fileContentResponse = await axios.get(
        `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          responseType: 'blob',
        },
      );
      const mimeType = fileContentResponse?.headers['content-type'];

      // Create a File object from the fetched data
      return new File([fileContentResponse.data], fileName, { type: mimeType });
    } catch (error) {
      console.log('Error fetching file data:', error);
      return null; // Return null if there's an error
    }
  });

  // Wait for all promises to resolve
  const uploadedFiles = await Promise.all(filePromises);

  // Filter out any null results due to errors
  return uploadedFiles.filter((file) => file !== null);
};

const handleFileUploadFilter = (fileList) => {
  const uploadEventFiles = fileList;
  const allowedFileTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'application/pdf',
  ];
  const maxSizeInBytes = 10 * 1024 * 1024; // max size allowed 10mb

  const multipleFilesArray = Array.from(uploadEventFiles);
  const filesFormatNotValid = multipleFilesArray.filter(
    (file) => !allowedFileTypes.includes(file.type),
  );
  filesFormatNotValid?.forEach((file) => {
    Toast({
      message: `"${file.name}" ${DOCUMENT_TOAST.FORMAT_INVALID}`,
      variant: 'error',
      position: { vertical: 'top', horizontal: 'center' },
      autoHideDuration: 4000,
    });
  });

  const filesFormatValid = multipleFilesArray.filter((file) =>
    allowedFileTypes.includes(file.type),
  );

  const filesSizeTooLarge = filesFormatValid?.filter(
    (file) => file.size > maxSizeInBytes,
  );
  filesSizeTooLarge?.forEach((file) => {
    Toast({
      message: `"${file.name}" ${DOCUMENT_TOAST.FILE_SIZE_TOO_LARGE}`,
      variant: 'error',
      position: { vertical: 'top', horizontal: 'center' },
      autoHideDuration: 4000,
    });
  });

  const sizeLimitFilesValid = filesFormatValid?.filter(
    (file) => file.size <= maxSizeInBytes,
  );

  const fileNamesWithSpacesOrSpecialChars = sizeLimitFilesValid.filter((file) =>
    /[ !@#$%^&*]/.test(file.name),
  );
  fileNamesWithSpacesOrSpecialChars?.forEach((file) => {
    Toast({
      message: `"${file.name}" ${DOCUMENT_TOAST.SPACES_OR_SPECIAL_CHARS_NOT_ALLOWED}`,
      variant: 'error',
      position: { vertical: 'top', horizontal: 'center' },
      autoHideDuration: 4000,
    });
  });
  return sizeLimitFilesValid.filter((file) =>
    /^[^\s!@#$%^&*]+$/.test(file.name),
  );
};

const googleDriveUpload = async (pickerApiLoaded, accessToken) =>
  new Promise((resolve, reject) => {
    if (pickerApiLoaded) {
      const view = new window.google.picker.DocsView().setMimeTypes(
        'image/jpeg,image/jpg,image/png,application/pdf',
      );
      const googleDrivePicker = new window.google.picker.PickerBuilder()
        .addView(view)
        .setOAuthToken(accessToken)
        .setDeveloperKey(GOOGLE_DRIVE_API_KEY)
        .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
        .setCallback(async (data) => {
          if (data.action === window.google.picker.Action.PICKED) {
            const fileIds = data.docs.map((doc) => doc?.id);
            const fileList = await uploadFiles(fileIds, accessToken);
            try {
              const files = handleFileUploadFilter(fileList);
              if (files && files.length > 0) {
                resolve(files);
              } else {
                resolve(null);
              }
            } catch (error) {
              console.error('Error downloading file from Google Drive:', error);
              reject(error);
            }
          }
        })
        .build();
      googleDrivePicker.setVisible(true);
    } else {
      console.error('Google Picker API is not loaded.');
    }
  });

export default googleDriveUpload;
